import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpcTanstackQueryUtils } from '../orpc'

export function usePassengerGetAll(filters?: {
  search?: string
  includeDeleted?: boolean
}) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.passenger.getAll.queryOptions({ input: { ...filters } }))
}

export function usePassengerGetById(id?: string) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.passenger.getById.queryOptions({ input: { id }, enabled: !!id }))
}

export function usePassengerGetByIds(ids: string[]) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery({
    ...orpc.passenger.getByIds.queryOptions({ input: { ids } }),
    enabled: ids.length > 0,
  })
}

export function usePassengerQuickAdd() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.passenger.quickAdd.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
      },
    })
  )
}

export function usePassengerCreate() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.passenger.create.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
      },
    })
  )
}

export function usePassengerUpdate(_passengerId: string) {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.passenger.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
      },
    })
  )
}

// Set (or clear) the passenger's profile photo S3 key.
export function usePassengerSetPhoto() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.passenger.setPhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
      },
    })
  )
}

// Attach / detach a photo on one of the passenger's preference sections.
export function usePassengerAddPreferencePhoto() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.passenger.addPreferencePhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
      },
    })
  )
}

export function usePassengerRemovePreferencePhoto() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.passenger.removePreferencePhoto.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
      },
    })
  )
}

export function usePassengerDelete() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.passenger.delete.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
      },
    })
  )
}

export function usePassengerSearch(query: string) {
  const orpc = useOrpcTanstackQueryUtils()
  return useQuery(orpc.passenger.search.queryOptions({ input: { query } }))
}

export function usePassengerRestore() {
  const orpc = useOrpcTanstackQueryUtils()
  const queryClient = useQueryClient()

  return useMutation(
    orpc.passenger.restore.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: orpc.passenger.key() })
      },
    })
  )
}
